import { defineStore, storeToRefs } from 'pinia';
import {
  BundleDetails,
  CodebaseProductData,
  LocalisedCodebaseProductData,
  ProductComplete,
  ShopifyProductData,
} from '../types/models';
import merge from 'deepmerge';
import { ref, computed } from 'vue';
import { codeProductData } from './rawData/codeProductData';
import { useStoreMediaLocaliseHelper } from '../composables/storeMediaLocaliseHelper';
import { useStoreContentLocaliseHelper } from '../composables/storeContentLocaliseHelper';
import { useProductBaseStore } from './productBase';
import { createProductMapKey } from '@/composables/productHelper';

const findProductByVariantId = (
  products: ProductComplete[],
  variantId: number,
): ProductComplete | undefined => {
  return products.find(({ attributes }) => attributes.variantId === variantId);
};

export const useProductBundleStore = defineStore('productBundleStore', () => {
  const cachedBundleProducts = ref<ShopifyProductData[]>([]);

  const productBaseStore = useProductBaseStore();
  const { completeBaseProducts } = storeToRefs(productBaseStore);

  const setCachedBundleProducts = (products: ShopifyProductData[]) => {
    try {
      if (!Array.isArray(products)) {
        throw new Error('Gotta be an array');
      }
      cachedBundleProducts.value = products;
    } catch (error) {
      console.error('Failed to set the cached base products...', error);
      throw error;
    }
  };

  const buildBundleDefinition = (
    bundleProduct: ProductComplete,
    completeBaseProducts: ProductComplete[],
  ): BundleDetails | null => {
    try {
      const bundleDetails = bundleProduct.attributes?.bundleDetails;
      if (!bundleDetails || !completeBaseProducts.length) {
        throw new Error('Failed to contain required data');
      }
      const {
        title,
        bundleType,
        pricingRules,
        additionalBundleDetails = null,
        minItems = null,
        maxItems = null,
        bundleItems,
      } = bundleDetails;
      const processedBundleItems = bundleItems.map((bundleGroup) => {
        const processedProducts = bundleGroup.productIds
          .map((variantId) => {
            return findProductByVariantId(completeBaseProducts, variantId);
          })
          .filter(Boolean);
        return { ...bundleGroup, processedProducts };
      });
      return {
        title,
        bundleType,
        pricingRules,
        bundleItems: processedBundleItems,
        ...(additionalBundleDetails && { additionalBundleDetails }),
        ...(minItems && { minItems }),
        ...(maxItems && { maxItems }),
      };
    } catch (error) {
      console.warn(error);
      return null;
    }
  };

  const buildCompleteBundleProduct = (cachedBundleProduct: ShopifyProductData): ProductComplete => {
    try {
      // Validate the product has the minimum data -> ref product base
      if (!cachedBundleProduct?.attributes?.sku || !cachedBundleProduct?.attributes?.handle) {
        throw new Error('Bundle product missing sku / handle');
      }

      // Step 1 -> find the product details in codebase
      const { sku, handle } = cachedBundleProduct.attributes;
      const mapKey = createProductMapKey(sku, handle);

      const codebaseBundleProduct: CodebaseProductData = codeProductData.get(mapKey);
      if (!codebaseBundleProduct) {
        throw new Error(`Bundle product missing codebase data for map key: ${mapKey}`);
      }

      // Step 2 -> localise these things
      let localisedMedia;
      try {
        localisedMedia = useStoreMediaLocaliseHelper(codebaseBundleProduct.media);
      } catch (error) {
        console.warn(`Failed to localise the media for bundle product ${handle}`, error);
      }
      // Step 3 -> Merge the localised media into the return object + cached data
      const localisedContent = useStoreContentLocaliseHelper(
        codebaseBundleProduct.content,
        codebaseBundleProduct.attributes.sku,
      );
      const localisedCodebaseBundleProduct: LocalisedCodebaseProductData = {
        ...codebaseBundleProduct,
        media: localisedMedia,
        content: localisedContent,
      };

      const mergedBundleProduct = merge<ProductComplete>(
        cachedBundleProduct as Partial<ProductComplete>,
        localisedCodebaseBundleProduct as Partial<ProductComplete>,
      );
      // Step 4 -> Build out the products that make up the bundle
      const bundleDefinition = buildBundleDefinition(
        mergedBundleProduct,
        completeBaseProducts.value,
      );
      const completeBundleProduct = {
        ...mergedBundleProduct,
        ...(bundleDefinition && { bundleDefinition }),
      };
      // Step 5 -> Return the object
      return completeBundleProduct;
      // Note -> final object should have all the bundleDefinition stuff built out
    } catch (error) {
      const handle = cachedBundleProduct?.attributes?.handle || 'unknown';
      throw new Error(`Failed to build bundle product for handle...${handle}...${error}`);
    }
  };

  const completeBundleProducts = computed<ProductComplete[]>(() => {
    const value: ProductComplete[] = [];
    for (const cachedBundleProduct of cachedBundleProducts.value) {
      try {
        const completeBundle = buildCompleteBundleProduct(cachedBundleProduct);
        value.push(completeBundle);
      } catch (error) {
        const handle = cachedBundleProduct?.attributes?.handle || 'unknown';
        console.error(`Failed to build complete bundle product for handle ${handle}...`, error);
      }
    }
    return value;
  });
  return {
    cachedBundleProducts,
    completeBundleProducts,
    setCachedBundleProducts,
    buildBundleDefinition,
  };
});
